"use client";

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Subscription as SubscriptionPlan } from "@/types/subscription.types";

export interface SubscriptionFilters {
  search: string;
  planId: string;
  startDate: string;
  endDate: string;
}

interface SubscriptionFilterProps {
  filters: SubscriptionFilters;
  onFilterChange: (filters: SubscriptionFilters) => void;
  plans: SubscriptionPlan[];
}

export function SubscriptionFilter({
  filters,
  onFilterChange,
  plans,
}: SubscriptionFilterProps) {
  const [searchInput, setSearchInput] = useState(filters.search);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFilterChange({ ...filters, search: searchInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters, onFilterChange]);

  // Sync search input with filters when filters change externally
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const handlePlanChange = useCallback(
    (value: string) => {
      onFilterChange({ ...filters, planId: value });
    },
    [filters, onFilterChange]
  );

  const handleStartDateChange = useCallback(
    (date: Date | undefined) => {
      onFilterChange({
        ...filters,
        startDate: date ? format(date, "yyyy-MM-dd") : "",
      });
    },
    [filters, onFilterChange]
  );

  const handleEndDateChange = useCallback(
    (date: Date | undefined) => {
      onFilterChange({
        ...filters,
        endDate: date ? format(date, "yyyy-MM-dd") : "",
      });
    },
    [filters, onFilterChange]
  );

  const handleClearAll = useCallback(() => {
    setSearchInput("");
    onFilterChange({
      search: "",
      planId: "all",
      startDate: "",
      endDate: "",
    });
  }, [onFilterChange]);

  const hasActiveFilters =
    filters.search ||
    (filters.planId && filters.planId !== "all") ||
    filters.startDate ||
    filters.endDate;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search customer, phone, plate..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Plan Type Dropdown */}
          <Select value={filters.planId || "all"} onValueChange={handlePlanChange}>
            <SelectTrigger>
              <SelectValue placeholder="All Plans" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Plans</SelectItem>
              {[...plans].sort((a, b) => a.name.localeCompare(b.name)).map((plan) => (
                <SelectItem key={plan.id} value={plan.id}>
                  {plan.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Start Date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !filters.startDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.startDate
                  ? format(new Date(filters.startDate), "PPP")
                  : "Start Date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={
                  filters.startDate ? new Date(filters.startDate) : undefined
                }
                onSelect={handleStartDateChange}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* End Date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !filters.endDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.endDate
                  ? format(new Date(filters.endDate), "PPP")
                  : "End Date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={
                  filters.endDate ? new Date(filters.endDate) : undefined
                }
                onSelect={handleEndDateChange}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Clear All Button */}
        {hasActiveFilters && (
          <div className="mt-4 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="mr-2 h-4 w-4" />
              Clear All Filters
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
